/**
 * AdminTokenTransactions - Admin view for Q.AI Token Payment Transactions
 * Displays all payment transactions across all users with filtering and context menu
 *
 * Features:
 * - Lists all Q.AI token purchases from all users
 * - Filter by status (pending, processing, accepted, rejected, expired)
 * - Search by user email or payment ID
 * - Context menu with Accept/Reject placeholder options
 * - Pagination
 */

import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    Avatar,
    Pagination,
    CircularProgress,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    MoreVert as MoreVertIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Receipt as ReceiptIcon,
    AttachMoney as AttachMoneyIcon,
    PendingActions as PendingActionsIcon,
    Token as TokenIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Block as BlockIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getAdminPayments, acceptPayment, rejectPayment } from '../../api/admin_api';

// Embedded theme (matching AdminQAIUserManagement)
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.7)', quaternary: 'rgba(255,255,255,0.5)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.05)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' }
    },
    gradients: { primaryAccent: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' },
    borderRadius: { sm: '8px', md: '12px' }
};

// Status display configuration
const STATUS_CONFIG = {
    pending: { label: 'Pending', color: '#FBBF24', bgColor: 'rgba(251, 191, 36, 0.15)', icon: PendingActionsIcon },
    processing: { label: 'Processing', color: '#3B82F6', bgColor: 'rgba(59, 130, 246, 0.15)', icon: HourglassEmptyIcon },
    accepted: { label: 'Completed', color: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.15)', icon: CheckCircleIcon },
    rejected: { label: 'Failed', color: '#EF4444', bgColor: 'rgba(239, 68, 68, 0.15)', icon: CancelIcon },
    expired: { label: 'Expired', color: '#71717A', bgColor: 'rgba(113, 113, 122, 0.15)', icon: BlockIcon },
    partial: { label: 'Partial', color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.15)', icon: PendingActionsIcon },
    refunded: { label: 'Refunded', color: '#71717A', bgColor: 'rgba(113, 113, 122, 0.15)', icon: CancelIcon }
};

const AdminTokenTransactions = () => {
    // State management
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [payments, setPayments] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [stats, setStats] = useState({
        totalTransactions: 0,
        completedTransactions: 0,
        pendingTransactions: 0,
        totalRevenue: 0
    });

    const rowsPerPage = 15;

    // Fetch payments
    const fetchPayments = useCallback(async (pageNum = 1) => {
        setLoading(true);
        try {
            const filters = {
                page: pageNum,
                limit: rowsPerPage,
                status: statusFilter !== 'all' ? statusFilter : undefined
            };
            const result = await getAdminPayments(filters);

            if (result.success) {
                const data = result.data;
                setPayments(data.payments || []);
                setTotalPages(Math.ceil((data.total || 0) / rowsPerPage));

                // Calculate stats from data
                const allPayments = data.payments || [];
                setStats({
                    totalTransactions: data.total || allPayments.length,
                    completedTransactions: allPayments.filter(p => p.status === 'accepted').length,
                    pendingTransactions: allPayments.filter(p => ['pending', 'processing'].includes(p.status)).length,
                    totalRevenue: allPayments
                        .filter(p => p.status === 'accepted')
                        .reduce((sum, p) => sum + (p.price_amount || 0), 0)
                });

                console.log('[AdminTokenTransactions] Loaded:', {
                    payments: allPayments.length,
                    total: data.total,
                    page: pageNum
                });
            } else {
                toast.error(result.error || 'Failed to fetch payment transactions');
            }
        } catch (error) {
            console.error('[AdminTokenTransactions] Error:', error);
            toast.error('Failed to fetch payment transactions');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    // Initial load
    useEffect(() => {
        fetchPayments(1);
    }, [fetchPayments]);

    // Handlers
    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchPayments(page);
        setRefreshing(false);
        toast.success('Data refreshed');
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        fetchPayments(newPage);
    };

    const handleMenuOpen = (event, payment) => {
        setAnchorEl(event.currentTarget);
        setSelectedPayment(payment);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleAccept = async () => {
        if (!selectedPayment) return;
        handleMenuClose();

        // Check if payment is in a valid state for acceptance
        if (selectedPayment.status === 'accepted') {
            toast.warning('Payment is already accepted');
            return;
        }

        try {
            const result = await acceptPayment(selectedPayment.id, 'Manual approval by admin');
            if (result.success) {
                toast.success(result.message || 'Payment accepted and tokens credited successfully');
                fetchPayments(page);
            } else {
                toast.error(result.error || 'Failed to accept payment');
            }
        } catch (error) {
            console.error('[AdminTokenTransactions] Accept error:', error);
            toast.error('Failed to accept payment');
        }
    };

    const handleReject = async () => {
        if (!selectedPayment) return;
        handleMenuClose();

        // Check if payment is in a valid state for rejection
        if (selectedPayment.status === 'rejected') {
            toast.warning('Payment is already rejected');
            return;
        }
        if (selectedPayment.status === 'accepted') {
            toast.warning('Cannot reject an accepted payment');
            return;
        }

        try {
            const result = await rejectPayment(selectedPayment.id, 'Rejected by admin');
            if (result.success) {
                toast.success(result.message || 'Payment rejected successfully');
                fetchPayments(page);
            } else {
                toast.error(result.error || 'Failed to reject payment');
            }
        } catch (error) {
            console.error('[AdminTokenTransactions] Reject error:', error);
            toast.error('Failed to reject payment');
        }
    };

    // Filter payments by search query
    const filteredPayments = payments.filter(payment => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            payment.id?.toString().includes(query) ||
            payment.user_email?.toLowerCase().includes(query) ||
            payment.user_name?.toLowerCase().includes(query)
        );
    });

    // Format helpers
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '-';
        return `$${Number(amount).toFixed(2)}`;
    };

    const getStatusConfig = (status) => {
        return STATUS_CONFIG[status?.toLowerCase()] || STATUS_CONFIG.pending;
    };

    return (
        <Box sx={{ p: 3, background: theme.colors.bg.primary, minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ color: theme.colors.text.primary, fontWeight: 700 }}>
                        Token Transactions
                    </Typography>
                    <Typography sx={{ color: theme.colors.text.quaternary, mt: 0.5 }}>
                        View all Q.AI token purchase transactions
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={refreshing}
                    sx={{
                        borderColor: theme.colors.border.primary,
                        color: theme.colors.text.secondary,
                        '&:hover': { borderColor: theme.colors.accent.primary }
                    }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ background: theme.colors.status.info, width: 48, height: 48 }}>
                                    <ReceiptIcon />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        Total Transactions
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontSize: '1.5rem', fontWeight: 700 }}>
                                        {stats.totalTransactions}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ background: theme.colors.status.success, width: 48, height: 48 }}>
                                    <CheckCircleIcon />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        Completed
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontSize: '1.5rem', fontWeight: 700 }}>
                                        {stats.completedTransactions}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ background: theme.colors.status.warning, width: 48, height: 48 }}>
                                    <PendingActionsIcon />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        Pending
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontSize: '1.5rem', fontWeight: 700 }}>
                                        {stats.pendingTransactions}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ background: theme.colors.accent.primary, width: 48, height: 48 }}>
                                    <AttachMoneyIcon />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        Total Revenue
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontSize: '1.5rem', fontWeight: 700 }}>
                                        ${stats.totalRevenue.toFixed(2)}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            placeholder="Search by payment ID or user email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme.colors.text.quaternary }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    background: theme.colors.bg.tertiary,
                                    '& fieldset': { borderColor: theme.colors.border.primary }
                                },
                                '& .MuiInputBase-input': { color: theme.colors.text.primary }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: theme.colors.text.quaternary }}>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Status"
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setPage(1);
                                }}
                                sx={{
                                    background: theme.colors.bg.tertiary,
                                    color: theme.colors.text.primary,
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.border.primary }
                                }}
                            >
                                <MenuItem value="all">All Status</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="processing">Processing</MenuItem>
                                <MenuItem value="accepted">Completed</MenuItem>
                                <MenuItem value="rejected">Failed</MenuItem>
                                <MenuItem value="expired">Expired</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Transactions Table */}
            <TableContainer component={Paper} sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                {loading && <LinearProgress />}
                <Table>
                    <TableHead>
                        <TableRow sx={{ background: theme.colors.bg.tertiary }}>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>ID</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>User</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Amount</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Tokens</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Date</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredPayments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <Typography sx={{ color: theme.colors.text.quaternary }}>
                                        {loading ? 'Loading...' : 'No transactions found'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredPayments.map((payment) => {
                                const statusConfig = getStatusConfig(payment.status);
                                const StatusIcon = statusConfig.icon;

                                return (
                                    <TableRow key={payment.id} hover sx={{ '&:hover': { background: theme.colors.bg.tertiary } }}>
                                        <TableCell>
                                            <Typography sx={{ color: theme.colors.text.secondary, fontFamily: 'monospace' }}>
                                                #{payment.id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar sx={{ background: theme.gradients.primaryAccent, width: 36, height: 36 }}>
                                                    {payment.user_email?.charAt(0).toUpperCase() || '?'}
                                                </Avatar>
                                                <Box>
                                                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 500 }}>
                                                        {payment.user_name || payment.user_email || 'Unknown'}
                                                    </Typography>
                                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                                        {payment.user_email || `User ID: ${payment.user_id}`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                                                {formatCurrency(payment.price_amount)}
                                            </Typography>
                                            <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                                {payment.price_currency?.toUpperCase() || 'USD'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={<TokenIcon sx={{ fontSize: 16 }} />}
                                                label={payment.tokens_to_credit || payment.tokens || '-'}
                                                sx={{
                                                    background: theme.colors.accent.primary + '20',
                                                    color: theme.colors.accent.tertiary,
                                                    fontWeight: 600
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={<StatusIcon sx={{ fontSize: 16 }} />}
                                                label={statusConfig.label}
                                                size="small"
                                                sx={{
                                                    background: statusConfig.bgColor,
                                                    color: statusConfig.color,
                                                    fontWeight: 500,
                                                    '& .MuiChip-icon': { color: statusConfig.color }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.85rem' }}>
                                                {formatDate(payment.created_at)}
                                            </Typography>
                                            {payment.paid_at && (
                                                <Typography sx={{ color: theme.colors.status.success, fontSize: '0.7rem' }}>
                                                    Paid: {formatDate(payment.paid_at)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                onClick={(e) => handleMenuOpen(e, payment)}
                                                sx={{ color: theme.colors.text.tertiary }}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: theme.colors.text.secondary,
                                '&.Mui-selected': {
                                    background: theme.colors.accent.primary,
                                    color: theme.colors.text.primary
                                }
                            }
                        }}
                    />
                </Box>
            )}

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        background: theme.colors.bg.secondary,
                        border: `1px solid ${theme.colors.border.primary}`,
                        minWidth: 180
                    }
                }}
            >
                <MenuItem onClick={handleAccept} sx={{ color: theme.colors.text.primary }}>
                    <ListItemIcon>
                        <CheckCircleIcon sx={{ color: theme.colors.status.success }} />
                    </ListItemIcon>
                    <ListItemText>Accept</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleReject} sx={{ color: theme.colors.text.primary }}>
                    <ListItemIcon>
                        <CancelIcon sx={{ color: theme.colors.status.error }} />
                    </ListItemIcon>
                    <ListItemText>Reject</ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default AdminTokenTransactions;
